import { autoUpdate, FloatingFocusManager, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'motion/react'
import { RefObject, useRef, useState } from 'react'

import { AccordionItemProps, AstraAccordion, AstraButton, AstraSegmentedControl, day, Spinner, subtract } from '~/astra'
import client from '~/clients/api-client'
import { filterInboxEntriesByDateRange, useInboxEntriesForFilter } from '~/storm'
import { trackUserEvent } from '~/utils/analytics/analytics'

import { BreadcrumbsHeader } from '../BreadcrumbsHeader'
import { EmptyInboxList } from './EmptyInboxList'
import { InboxList } from './InboxList'
import { useImportInboxEntriesQuery, useUnreadInboxEntriesQuery } from './useInbox'
import { useInboxDrawer } from './useInboxDrawer'

const getInitialInboxFilter = (unreadInboxEntriesCount: number | undefined) => {
    if (unreadInboxEntriesCount === 0) return 'all'
    return 'unread'
}

const InboxDrawerContents = ({ closeDrawer }: { closeDrawer: () => void }) => {
    const { isLoading } = useImportInboxEntriesQuery()
    const listRef = useRef<HTMLDivElement>(null)

    const { data: unreadInboxEntriesCount } = useUnreadInboxEntriesQuery()

    const [inboxFilter, setInboxFilter] = useState<'all' | 'unread'>(getInitialInboxFilter(unreadInboxEntriesCount))

    const markAllInboxEntriesAsRead = useMutation({
        mutationFn: async () => client.markAllInboxEntriesAsRead(),
        onSuccess: () => {
            trackUserEvent('INBOX_MARKED_ALL_AS_READ', { unreadCount: unreadInboxEntriesCount ?? 0 })
        },
    })

    // we want actual time today, and not potential mocked date from getToday()
    const today = day().startOf('day')

    const inboxEntries = useInboxEntriesForFilter(inboxFilter)

    const dateRanges = {
        thisWeek: [today.startOf('isoWeek'), today.endOf('isoWeek')] as const,
        lastWeek: [subtract(today, 1, 'week').startOf('isoWeek'), subtract(today, 1, 'week').endOf('isoWeek')] as const,
        older: [null, subtract(today, 2, 'week').endOf('isoWeek')] as const,
    }

    const thisWeekEntries = filterInboxEntriesByDateRange(inboxEntries, dateRanges.thisWeek[0], dateRanges.thisWeek[1])
    const lastWeekEntries = filterInboxEntriesByDateRange(inboxEntries, dateRanges.lastWeek[0], dateRanges.lastWeek[1])
    const olderEntries = filterInboxEntriesByDateRange(inboxEntries, dateRanges.older[0], dateRanges.older[1])

    const accordionItems = [
        {
            value: 'thisWeek',
            title: 'Denne uken',
            content: <InboxList entries={thisWeekEntries} onClose={closeDrawer} listRef={listRef} />,
            hide: thisWeekEntries.length === 0,
            overflow: false,
        },
        {
            value: 'lastWeek',
            title: 'Forrige uke',
            content: <InboxList entries={lastWeekEntries} onClose={closeDrawer} listRef={listRef} />,
            hide: lastWeekEntries.length === 0,
        },
        {
            value: 'older',
            title: 'Eldre',
            content: <InboxList entries={olderEntries} onClose={closeDrawer} listRef={listRef} />,
            hide: olderEntries.length === 0,
        },
    ] satisfies AccordionItemProps<string>[]

    return (
        <div className="relative flex h-full min-w-[440px] flex-col">
            <BreadcrumbsHeader className="shrink-0 py-2" crumbs={[{ label: 'Innboks' }]} onClose={closeDrawer} />
            <div className="flex items-center justify-between border-b border-subtle px-3 py-2 text-default">
                <AstraSegmentedControl
                    controlOptions={[
                        { value: 'all', label: 'Alle' },
                        { value: 'unread', label: 'Uleste' },
                    ]}
                    value={inboxFilter}
                    onValueChange={value => setInboxFilter(value as typeof inboxFilter)}
                />
                <AstraButton
                    variant="ghost"
                    label={`Merk alle som lest (${unreadInboxEntriesCount ?? 0})`}
                    onClick={() => markAllInboxEntriesAsRead.mutate()}
                    disabled={markAllInboxEntriesAsRead.isPending}
                    isLoading={markAllInboxEntriesAsRead.isPending}
                />
            </div>
            <div className="flex flex-col gap-1.5 overflow-auto" ref={listRef}>
                {isLoading ? (
                    <Spinner className="mx-auto mt-8" size="lg" />
                ) : (
                    inboxEntries.length === 0 && <EmptyInboxList showAllClick={inboxFilter === 'unread' ? () => setInboxFilter('all') : undefined} />
                )}
                <AstraAccordion defaultOpen={accordionItems.map(item => item.value)} items={accordionItems.filter(item => !item.hide)} />
            </div>
        </div>
    )
}

const FloatingInboxDrawer = ({
    sidebarRef,
    openDrawer,
    closeDrawer,
}: {
    sidebarRef: RefObject<HTMLDivElement | null>
    openDrawer: () => void
    closeDrawer: () => void
}) => {
    const { refs, floatingStyles, context } = useFloating({
        open: true,
        onOpenChange: open => (open ? openDrawer() : closeDrawer()),
        placement: 'right',
        elements: {
            reference: sidebarRef.current,
        },
        whileElementsMounted: autoUpdate,
    })

    const dismiss = useDismiss(context)
    const { getFloatingProps } = useInteractions([dismiss])

    return (
        <motion.div
            initial={{ width: 0 }}
            animate={{ width: 440 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ ease: 'easeInOut', duration: 0.2 }}
            className="h-[100vh] overflow-hidden border-none bg-surface-base shadow-[4px_0_15px_-3px_rgba(0,0,0,0.1),2px_0_6px_-2px_rgba(0,0,0,0.1)]"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
        >
            <FloatingFocusManager context={context} modal={false}>
                <InboxDrawerContents closeDrawer={closeDrawer} />
            </FloatingFocusManager>
        </motion.div>
    )
}

export const InboxDrawer = ({ sidebarRef }: { sidebarRef: RefObject<HTMLDivElement | null> }) => {
    const { isDrawerOpen, openDrawer, closeDrawer } = useInboxDrawer()
    if (!isDrawerOpen) return null

    return <FloatingInboxDrawer sidebarRef={sidebarRef} openDrawer={openDrawer} closeDrawer={closeDrawer} />
}
